import * as React from 'react'
import { Focusable } from './Focusable'
import { classNames } from './helpers'
import type { LinkComponent } from '../types'

export interface BreadcrumbItem {
  /** What the crumb reads as. Translated by the caller — the styleguide ships no copy. */
  label: string;
  /**
   * Where the crumb leads. The last crumb is the page you are on, so it has no
   * `href`; a crumb without one renders as plain text.
   */
  href?: string;
}

export interface BreadcrumbProps {
  /** The trail, outermost first (e.g. Home → the section this page belongs to). */
  items: BreadcrumbItem[];
  /**
   * In-app link component (e.g. React Router's `Link`). Without it every crumb
   * renders as text, since the styleguide holds no router of its own.
   */
  LinkComponent?: LinkComponent;
  /**
   * Accessible name for the `<nav>` (e.g. "Breadcrumb") — a page can hold several
   * navigations, and this is what tells them apart in a landmark list.
   */
  label?: string;
  className?: string;
}

/**
 * The trail at the head of a detail page — "Home / News" — shared by the topic,
 * article and title pages so all three place the viewer the same way.
 *
 * A list, not a row of links: the order is the meaning, and `aria-current` marks the
 * last crumb as the page itself. The separators are drawn in CSS, so they stay out
 * of the text a screen reader announces.
 */
export function Breadcrumb ({ items, LinkComponent, label, className }: BreadcrumbProps) {
  return (
    <nav className={classNames('mlsg-breadcrumb', className)} aria-label={label}>
      <ol className='mlsg-breadcrumb__list'>
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          return (
            <li className='mlsg-breadcrumb__item' key={`${item.label}-${index}`}>
              {item.href && LinkComponent && !isLast
                ? (
                  // Its own stop, so a remote can walk out of a detail page the same
                  // way it walked in.
                  <Focusable className='mlsg-focusable--inline'>
                    <LinkComponent to={item.href} className='mlsg-breadcrumb__link'>{item.label}</LinkComponent>
                  </Focusable>
                  )
                : <span className='mlsg-breadcrumb__current' aria-current={isLast ? 'page' : undefined}>{item.label}</span>}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
